import { Dispatch } from 'react';
import { Location, NavigateFunction } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Stack, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { signOut, User } from 'firebase/auth';
import { applyActionCode, getAuth } from 'firebase/auth';
import { TFunction } from 'i18next';

import { ErrorResponseDto, IWhoami } from '@mopla/data-models';
import {
	PASSENGER_ANDROID_LINK,
	PASSENGER_IOS_LINK,
	StoreBadges,
} from '@mopla/ui';

import passApi from '../../../api/passApi';

import { VerificationStatus, WelcomeStep } from './constants';
import { TWelcomePageStoreAction } from './useWelcomePageStore';

export const RejectNoUserDataTitle = (
	t: TFunction,
	rejectType: 'generic' | 'linkExpired'
) => {
	return (
		<>
			<Typography
				variant="b1"
				color="primary.main"
				data-testid="registration-somethingWentWrong-text"
			>
				{rejectType === 'linkExpired'
					? t('verify:text.title.link_expired')
					: t('verify:text.title.something_went_wrong')}
			</Typography>
			<Typography variant="b1" color="primary.main">
				{t('verify:text.title.please_login_again')}
			</Typography>
		</>
	);
};

export const SuccessNotSameDeviceAppTitle = (
	t: TFunction,
	successType: 'registration' | 'emailChange'
) => {
	return (
		<Typography
			variant="b1"
			color="primary.main"
			data-testid="success-not-same-device-app-title"
		>
			{successType === 'emailChange'
				? t('verify:text.title.email_changed')
				: t('verify:text.title.sign_up_right_now')}
		</Typography>
	);
};

export const SuccessNotSameDeviceWebTitle = (
	t: TFunction,
	successType: 'registration' | 'emailChange'
) => {
	return (
		<Stack gap={3}>
			<Typography
				variant="b1"
				color="primary.main"
				data-testid="success-not-same-device-web-title"
				textAlign="center"
			>
				{successType === 'emailChange'
					? t('verify:text.title.email_changed')
					: t('verify:text.title.account_verified')}
				{'\n'}
				{t('verify:text.title.already_have_app')}
			</Typography>
			<StoreBadges
				iosLink={PASSENGER_IOS_LINK}
				androidLink={PASSENGER_ANDROID_LINK}
				sx={{ mb: 0 }}
			/>
			<Typography mt={1} variant="b1" color="primary.main" textAlign="center">
				{t('verify:text.title.or_login_in_browser')}
			</Typography>
		</Stack>
	);
};

export const verifyEmail = (
	location: Location,
	navigate: NavigateFunction,
	user: User,
	t: TFunction,
	verificationStatus: VerificationStatus | null,
	loading: boolean,
	dispatch: Dispatch<TWelcomePageStoreAction>
) => {
	const auth = getAuth();
	const oobCode = new URLSearchParams(location.search).get('oobCode');
	return applyActionCode(auth, oobCode as string)
		.then(() => {
			if (auth.currentUser) {
				passApi
					.get('/api/whoami')
					.then((res: IWhoami) => {
						if (res.pleaseComplete) {
							return navigate(
								{
									pathname: '/welcome',
									search: 'personalDetails',
								},
								{ state: { title: 'sameDevice' } }
							);
						} else {
							return navigate('/home');
						}
					})
					.catch((err: ErrorResponseDto) => console.log(err));
				user?.reload().then(() => getAuth());
			} else {
				if (Capacitor.isNativePlatform()) {
					dispatch({
						type: 'SET_LOGIN_TITLE',
						payload: SuccessNotSameDeviceAppTitle(t, 'registration'),
					});

					dispatch({
						type: 'SET_VERIFICATION_STATUS',
						payload: VerificationStatus.successNotSameDeviceApp,
					});
				} else {
					dispatch({
						type: 'SET_LOGIN_TITLE',
						payload: SuccessNotSameDeviceWebTitle(t, 'registration'),
					});

					dispatch({
						type: 'SET_VERIFICATION_STATUS',
						payload: VerificationStatus.successNotSameDeviceWeb,
					});
				}
				dispatch({ type: 'SET_STEP', payload: WelcomeStep.login });
			}
		})
		.catch((e) => {
			if (!auth.currentUser) {
				dispatch({
					type: 'SET_VERIFICATION_STATUS',
					payload: VerificationStatus.rejectNoUserData,
				});

				dispatch({
					type: 'SET_LOGIN_TITLE',
					payload: RejectNoUserDataTitle(t, 'generic'),
				});

				dispatch({ type: 'SET_STEP', payload: WelcomeStep.login });
			} else if (
				auth.currentUser &&
				verificationStatus !== VerificationStatus.rejectNoUserData &&
				!loading
			) {
				dispatch({
					type: 'SET_VERIFICATION_STATUS',
					payload: VerificationStatus.rejectWithUserData,
				});

				dispatch({
					type: 'SET_VERIFY_FAILURE',
					payload: true,
				});
			}
		})
		.finally(() => {
			dispatch({ type: 'SET_IS_LOADING', payload: false });
		});
};

export const changeEmail = async (
	navigate: NavigateFunction,
	queryCode: string,
	t: TFunction,
	location: Location,
	dispatch: Dispatch<TWelcomePageStoreAction>
) => {
	const auth = getAuth();

	try {
		dispatch({ type: 'SET_IS_LOADING', payload: true });

		await passApi.post('/api/users/email/validate', { code: queryCode });

		// Removing the code from the URL then changing back the WelcomeScreen state after the API call has been made
		window.history.replaceState({}, document.title, location.pathname);

		// Sometimes the user is not signed out after the POST to the validate endpoint, but he will be the first time he refreshes the page
		// Therefore forcing signOut to avoid strange behaviour
		await signOut(auth);

		if (Capacitor.isNativePlatform()) {
			dispatch({
				type: 'SET_LOGIN_TITLE',
				payload: SuccessNotSameDeviceAppTitle(t, 'emailChange'),
			});
		} else {
			dispatch({
				type: 'SET_LOGIN_TITLE',
				payload: SuccessNotSameDeviceWebTitle(t, 'emailChange'),
			});
		}

		dispatch({ type: 'SET_STEP', payload: WelcomeStep.login });
	} catch (err) {
		console.error(err);
		if (err instanceof AxiosError) {
			const errorCode: string = err.response?.data?.code || '';
			switch (errorCode) {
				case 'REQUEST_EXPIRED':
					if (!auth.currentUser) {
						dispatch({
							type: 'SET_LOGIN_TITLE',
							payload: RejectNoUserDataTitle(t, 'linkExpired'),
						});
						dispatch({ type: 'SET_STEP', payload: WelcomeStep.login });
					} else {
						navigate('/home/profile?changeEmail=expired');
					}
					break;
				//Any other error code than REQUEST_EXPIRED gets a default failure screen
				//Other possible error codes are USER_NOT_FOUND and REQUEST_NOT_FOUND
				default:
					if (!auth.currentUser) {
						dispatch({
							type: 'SET_LOGIN_TITLE',
							payload: RejectNoUserDataTitle(t, 'generic'),
						});

						dispatch({ type: 'SET_STEP', payload: WelcomeStep.login });
					} else {
						navigate('/home/profile?changeEmail=failure');
					}
					break;
			}
		}
	} finally {
		dispatch({ type: 'SET_IS_LOADING', payload: false });
	}
};
